import * as Yup from 'yup';

// Define validation rules per type

const validationRules = {
  // string: (field, validation) => {
  //   let schema = Yup.string().nullable();

  //   if (validation?.required) {
  //     schema = schema.required(`${field.label} is required`);
  //   }
  //   if (validation?.minLength) {
  //     schema = schema.min(
  //       validation.minLength,
  //       `${field.label} must be at least ${validation.minLength} characters`
  //     );
  //   }
  //   if (validation?.maxLength) {
  //     schema = schema.max(
  //       validation.maxLength,
  //       `${field.label} must be at most ${validation.maxLength} characters`
  //     );
  //   }
  //   if (validation?.pattern) {
  //     schema = schema.test({
  //       name: 'pattern',
  //       message:
  //         validation?.messages?.pattern || `Invalid ${field.label} format`,
  //       test: value => !value || new RegExp(validation.pattern).test(value)
  //     });
  //   }
  //   return schema;
  // },

  number: (field, validation) => {
    let schema = Yup.number()
      .transform(
        (value, originalValue) => (originalValue === '' ? null : value) // Convert "" to null
      )
      .nullable()
      .typeError(`${field.label} must be a number`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minValue !== undefined) {
      schema = schema.min(
        validation.minValue,
        `${field.label} must be at least ${validation.minValue}`
      );
    }
    if (validation?.maxValue !== undefined) {
      schema = schema.max(
        validation.maxValue,
        `${field.label} must be at most ${validation.maxValue}`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value =>
          value === null ||
          value === undefined ||
          (typeof value === 'number' &&
            new RegExp(validation.pattern).test(String(value)))
      });
    }
    // Decimal places validation
    if (validation?.maxDecimalPlaces !== undefined) {
      schema = schema.test({
        name: 'maxDecimalPlaces',
        message: `${field.label} should not accept more than 2 decimal values`,
        test: value => {
          if (value === null || value === undefined || value === '')
            return true;
          if (Number.isNaN(value)) return false;
          const decimal = value.toString().split('.')[1];
          return !decimal || decimal.length <= validation?.maxDecimalPlaces;
        }
      });
    }
    return schema;
  },

  date: (field, validation) => {
    let schema = Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        // Return null if empty string, null, undefined, or an Invalid Date object
        if (
          !originalValue ||
          String(originalValue).trim() === '' ||
          String(value) === 'Invalid Date' ||
          (value instanceof Date && isNaN(value.getTime()))
        ) {
          return null;
        }
        return value;
      })
      .typeError(`${field.label} must be a valid date`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  email: (field, validation) => {
    let schema = Yup.string().nullable().email('Invalid email format');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  dropdown: (field, validation) => {
    if (field.multiSelect) {
      let schema = Yup.array()
        .nullable()
        .transform((value, originalValue) => originalValue || []);

      if (validation?.required) {
        schema = schema
          .min(1, `Please select at least one ${field.label.toLowerCase()}`)
          .required(`${field.label} is required`);
      }
      return schema;
    }

    let schema = Yup.string().nullable();
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    return schema;
  },

  textAndUnit: (field, validation) => {
    const objectShape = {
      // Quantity field validation - Accept both string and number
      quantity: Yup.mixed()
        .nullable()
        .transform((value, originalValue) => {
          // Handle various input types
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return null;
          }

          // If it's already a number, convert to string to ensure consistency
          if (typeof originalValue === 'number') {
            return originalValue.toString();
          }

          // Clean up string input
          if (typeof originalValue === 'string') {
            // Remove quotes if present
            const cleaned =
              originalValue.startsWith('"') && originalValue.endsWith('"')
                ? originalValue.slice(1, -1)
                : originalValue;

            // Return the cleaned string (not parsing to number)
            return cleaned;
          }

          // If we got here, convert to string for consistency
          return String(value);
        })
        // Define a custom test to validate it's a valid number
        .test(
          'is-valid-number',
          `${field.label} quantity must be a valid number`,
          value => {
            if (value === null || value === undefined || value === '')
              return true;
            const numValue = parseFloat(value);
            return !isNaN(numValue) && isFinite(numValue);
          }
        ),

      // Unit field validation (string/dropdown selection)
      unit: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          // Clean up any extra quotes
          if (
            typeof originalValue === 'string' &&
            originalValue.startsWith('"') &&
            originalValue.endsWith('"')
          ) {
            return originalValue.slice(1, -1);
          }
          return originalValue;
        })
    };

    // Apply required validation if specified
    if (validation?.required) {
      objectShape.quantity = objectShape.quantity.required(
        `${field.label} quantity is required`
      );
      objectShape.unit = objectShape.unit.required(
        `${field.label} unit is required`
      );
    }

    // Apply additional validation rules for quantity if specified
    if (validation?.minValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'min-value',
        `${field.label} quantity must be at least ${validation.minValue}`,
        value => {
          if (value === null || value === undefined || value === '')
            return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue >= validation.minValue;
        }
      );
    }
    if (validation?.maxValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'max-value',
        `${field.label} quantity must be less than or equal to ${validation.maxValue}`,
        value => {
          if (value === null || value === undefined || value === '')
            return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue <= validation.maxValue;
        }
      );
    }

    let schema = Yup.object().shape(objectShape);

    // Make the entire object required if validation requires it
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }

    return schema;
  },
  contact_number: (field, validation) => {
    let schema = Yup.string()
      .nullable()
      .matches(/^\d*$/, 'Phone number must only contain digits');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be exactly ${validation.minLength} digits`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be exactly ${validation.maxLength} digits`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },
  minmax: (field, validation) => {
    return Yup.object().shape({
      min: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        )
        .nullable()
        .typeError(`${field.label} Min must be a number`)
        .min(0, `${field.label} Min must be greater than or equal to 0`)
        .test(
          'both-or-none',
          `${field.label} Min is required if Max is filled`,
          function (value) {
            const { max } = this.parent;
            return !max || value !== null; // Min is required only if Max is present
          }
        ),

      max: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        )
        .nullable()
        .typeError(`${field.label} Max must be a number`)
        .test(
          'both-or-none',
          `${field.label} Max is required if Min is filled`,
          function (value) {
            const { min } = this.parent;
            return !min || value !== null; // Max is required only if Min is present
          }
        )
        .test(
          'greater-than-min',
          `${field.label} Max must be greater than Min`,
          function (value) {
            const { min } = this.parent;
            if (value === null || min === null) return true; // Skip validation if either is null
            return value >= min; // Ensure max is greater than or equal to min
          }
        )
    });
  },

  geo_location: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-location', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  coordinates: () => Yup.mixed().nullable(),

  table: field => {
    return Yup.array()
      .of(
        Yup.object().shape(
          field.table.columns.reduce((acc, column) => {
            if (column.validation) {
              acc[column.id] = getFieldValidator(column);
            }
            return acc;
          }, {})
        )
      )
      .min(1, `At least one row is required in ${field.table.title}`);
  },

  toggle_with_dropdown: field => {
    const schema = {
      [field.id]: Yup.string().required('Please select an option').nullable()
    };

    // Add dependent validations for dropdowns
    Object.entries(field.options).forEach(([key, dropdown]) => {
      schema[key] = Yup.string().when(field.id, {
        is: value => value === key,
        then: () =>
          Yup.string()
            .required(`Please select a ${dropdown.label.toLowerCase()}`)
            .nullable(),
        otherwise: () => Yup.string().nullable()
      });
    });

    return schema;
  },

  date_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-date_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  month_day_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-month_day_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  password: (field, validation) => {
    let schema = Yup.string().nullable();

    if (validation?.required) {
      schema = schema.required(`${field.label || 'Password'} is required`);
    }
    schema = schema
      .min(
        validation?.minLength || 8,
        `${field.label || 'Password'} must be at least ${
          validation?.minLength || 8
        } characters`
      )
      .matches(
        /[A-Z]/,
        validation?.messages?.uppercase ||
          `${
            field.label || 'Password'
          } must contain at least one uppercase letter`
      )
      .matches(
        /\d/,
        validation?.messages?.number ||
          `${field.label || 'Password'} must contain at least one number`
      )
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        validation?.messages?.specialCharacter ||
          `${
            field.label || 'Password'
          } must contain at least one special character`
      )
      .oneOf(
        [Yup.ref(validation?.reference || 'password_hash'), null],
        validation?.messages?.match ||
          `${field.label || 'Confirm Password'} must match Password`
      );

    return schema;
  },
  form_report: (field, validation) => {
    // Handle based on isMulti flag
    if (field.isMulti === false) {
      // For single value - use string validation
      let schema = Yup.string().nullable();

      if (validation?.required) {
        schema = schema.required(`${field.label} is required`);
      }

      return schema;
    } else {
      // For multi value (default) - use array validation
      let schema = Yup.array()
        .of(Yup.string().nullable())
        .nullable()
        .transform((value, originalValue) => originalValue || []);

      if (validation?.required) {
        schema = schema
          .min(1, `${field.label} must have at least one item`)
          .required(`${field.label} is required`);
      }

      return schema;
    }
  },
  jsonb: (field, validation) => {
    // Handle jsonb fields (modal data) as arrays
    let schema = Yup.array()
      .nullable()
      .transform((value, originalValue) => {
        // Handle various input types
        if (originalValue === null || originalValue === undefined) {
          return [];
        }
        if (Array.isArray(originalValue)) {
          return originalValue;
        }
        // If it's a string, try to parse it
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return [];
          }
        }
        // Convert single objects to arrays
        if (typeof originalValue === 'object') {
          return [originalValue];
        }
        return [];
      });

    if (validation?.required || field.error_message) {
      schema = schema
        .min(1, field.error_message || `${field.label} is required`)
        .required(field.error_message || `${field.label} is required`);
    }

    return schema;
  },
  string: (field, validation) => {
    let schema = Yup.string().nullable();
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be at least ${validation.minLength} characters`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be at most ${validation.maxLength} characters`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },
  Selection: (field, validation) => {
    let schema = Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required('Screen ID is required'),
          screen: Yup.string().required('Screen name is required'),
          module_id: Yup.string().required('Module ID is required'),
          action: Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required('Action ID is required'),
                name: Yup.string()
                  .oneOf(
                    ['Create', 'Edit', 'View'],
                    'Action name must be Create, Edit, or View'
                  )
                  .required('Action name is required'),
                value: Yup.string()
                  .oneOf(
                    ['true', 'false', 'hide'],
                    'Action value must be true, false, or hide'
                  )
                  .required('Action value is required')
              })
            )
            .min(
              3,
              'Each screen must have at least 3 actions (Create, Edit, View)'
            )
            .required('Action array is required')
        })
      )
      .default([]) // Better than transform for default values
      .transform((value, originalValue) => {
        // Only transform if it's truly null/undefined
        if (originalValue === null || originalValue === undefined) {
          return undefined; // Let Yup handle it with default
        }
        // Return as-is if it's already an array
        return Array.isArray(originalValue) ? originalValue : [originalValue];
      });

    if (validation?.required) {
      schema = schema
        .min(1, `${field.label} must have at least one selection`)
        .required(`${field.label} is required`);
    }

    return schema;
  },
  check_box: (field, validation) => {
    // Clean function to remove extra quotes
    const cleanValue = value => {
      if (
        typeof value === 'string' &&
        value.startsWith('"') &&
        value.endsWith('"')
      ) {
        return value.slice(1, -1);
      }
      return value;
    };

    if (field.multiSelect) {
      // For multi-select checkboxes, return array validation
      let schema = Yup.array()
        .of(Yup.string())
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null || originalValue === undefined) {
            return [];
          }
          if (Array.isArray(originalValue)) {
            // Clean each value in the array
            return originalValue.map(cleanValue);
          }
          // Convert single value to array
          return [cleanValue(originalValue)];
        });

      if (validation?.required) {
        schema = schema
          .min(1, `Please select at least one ${field.label.toLowerCase()}`)
          .required(`${field.label} is required`);
      }

      return schema;
    } else {
      // For single-select checkboxes, return string validation
      let schema = Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === null || originalValue === undefined) {
            return null;
          }
          if (Array.isArray(originalValue)) {
            // If array, take first value and clean it
            return originalValue.length > 0
              ? cleanValue(originalValue[0])
              : null;
          }
          return cleanValue(originalValue);
        });

      if (validation?.required) {
        schema = schema.required(`${field.label} is required`);
      }

      return schema;
    }
  }
};

// FIXED: Enhanced visibility calculation function
export const calculateFieldVisibility = (fields, formValues) => {
  const hiddenFields = new Set();
  const fieldList = Object.values(fields);

  // Handle radio_group hide functionality
  fieldList.forEach(field => {
    if (field.type === 'radio_group' && field.hide) {
      const selectedValue = formValues[field.id] ?? field.defaultValue;

      Object.entries(field.hide).forEach(([radioVal, hideArr]) => {
        // Hide fields that are listed for the CURRENTLY SELECTED radio value
        if (selectedValue === radioVal) {
          hideArr.forEach(hideFieldId => {
            hiddenFields.add(hideFieldId);
          });
        }
      });
    }
  });

  // Handle toggle fields with hide options
  fieldList.forEach(toggleField => {
    if (
      toggleField.type === 'toggle' &&
      Array.isArray(toggleField.hide) &&
      toggleField.hide.length > 0
    ) {
      const toggleValue =
        formValues[toggleField.id] ?? toggleField.defaultValue ?? false;
      const isToggleOn =
        toggleValue === true || toggleValue === 1 || toggleValue === '1';

      if (Array.isArray(toggleField.hide[0])) {
        const hideOnToggleOn = toggleField.hide[0];
        const hideOnToggleOff = toggleField.hide[1] || [];

        if (isToggleOn) {
          hideOnToggleOn.forEach(hiddenFieldId =>
            hiddenFields.add(hiddenFieldId)
          );
        } else {
          hideOnToggleOff.forEach(hiddenFieldId =>
            hiddenFields.add(hiddenFieldId)
          );
        }
      } else {
        toggleField.hide.forEach((hiddenFieldId, index) => {
          if (isToggleOn ? index === 0 : index === 1) {
            hiddenFields.add(hiddenFieldId);
          }
        });
      }
    }
  });

  // Handle visibilityRule for individual fields - ONLY hide if rule evaluates to FALSE
  fieldList.forEach(field => {
    if (field.visibilityRule) {
      try {
        const isVisible = new Function(
          'formValues',
          `return ${field.visibilityRule}`
        )(formValues);
        // If visibilityRule returns FALSE, the field should be hidden
        if (!isVisible) {
          hiddenFields.add(field.id);
        }
      } catch (e) {
        console.error(
          `Error evaluating visibility rule for field ${field.id}:`,
          e
        );
        // On error, assume field should be visible (don't hide it)
      }
    }
  });

  return hiddenFields;
};

// FIXED: Updated validation schema generation
export const generateValidationSchema = ({ fields, formValues = {} }) => {
  const schemaFields = {};
  const fieldList = Object.values(fields);

  // Calculate which fields should be hidden
  const hiddenFields = calculateFieldVisibility(fields, formValues);

  console.log('Hidden fields during validation:', Array.from(hiddenFields));

  fieldList.forEach(field => {
    // CRITICAL: Skip validation entirely for hidden fields
    if (hiddenFields.has(field.id)) {
      console.log(`Skipping validation for hidden field: ${field.id}`);
      return;
    }

    if (field.table) {
      schemaFields[field.id] = validationRules.table(field);
    } else if (field.type === 'toggle_with_dropdown') {
      Object.assign(schemaFields, validationRules.toggle_with_dropdown(field));
    } else if (field.type === 'jsonb') {
      schemaFields[field.id] = validationRules.jsonb(field, field.validation);
    } else {
      schemaFields[field.id] = getFieldValidator(field);
    }
  });

  return Yup.object().shape(schemaFields);
};

const getFieldValidator = field => {
  const validationRule = validationRules[field.type] || validationRules.string;
  return validationRule(field, field.validation);
};
